import { useLocale } from '../../i18n/LocaleProvider';
import type { OpsNode } from '../../lib/infrastructure-api';
import {
  formatDuration,
  formatNumber,
  formatPercent,
  levelLabelKey,
  nodeStatusLabelKey,
  nodeStatusTone,
  shortLabel,
} from '../../lib/infrastructure';
import { Badge } from '../Pill';

/**
 * Every node as a plain table — the cards' accessible twin. Same figures, no gauges, the level
 * word next to every percentage so nothing depends on colour.
 */
export function NodeTable({
  nodes,
  nodePrefix,
}: {
  readonly nodes: readonly OpsNode[];
  readonly nodePrefix: string;
}) {
  const { t, locale } = useLocale();
  const cell = 'py-2.5 pr-4 align-top';
  const percentWithLevel = (
    value: number | null,
    level: OpsNode['cpuLevel'],
  ): string => `${formatPercent(value, locale)} · ${t(levelLabelKey(level))}`;
  return (
    <div className="surface overflow-x-auto p-5">
      <table className="data-table min-w-[40rem]">
        <caption className="sr-only">
          {t('infrastructure.table.caption')}
        </caption>
        <thead>
          <tr>
            <th className={cell}>{t('infrastructure.table.columns.node')}</th>
            <th className={cell}>{t('infrastructure.table.columns.status')}</th>
            <th className={cell}>{t('infrastructure.table.columns.cpu')}</th>
            <th className={cell}>{t('infrastructure.table.columns.memory')}</th>
            <th className={cell}>{t('infrastructure.table.columns.disk')}</th>
            <th className={cell}>{t('infrastructure.table.columns.load')}</th>
            <th className={cell}>{t('infrastructure.table.columns.uptime')}</th>
          </tr>
        </thead>
        <tbody className="tabular-nums text-gray-700">
          {nodes.map((node) => (
            <tr key={node.name}>
              <th
                scope="row"
                className={`${cell} text-left font-medium text-gray-900`}
                title={node.name}
              >
                {shortLabel(node.name, nodePrefix)}
                <span className="block text-xs font-normal text-gray-500">
                  {node.role}
                </span>
              </th>
              <td className={cell}>
                <Badge tone={nodeStatusTone(node.status)}>
                  {t(nodeStatusLabelKey(node.status))}
                </Badge>
              </td>
              <td className={cell}>
                {percentWithLevel(node.cpuPercent, node.cpuLevel)}
              </td>
              <td className={cell}>
                {percentWithLevel(node.memoryPercent, node.memoryLevel)}
              </td>
              <td className={cell}>
                {percentWithLevel(node.diskPercent, node.diskLevel)}
              </td>
              <td className={cell}>
                {formatNumber(node.load1, locale, 2)} /{' '}
                {formatNumber(node.load5, locale, 2)} /{' '}
                {formatNumber(node.load15, locale, 2)}
              </td>
              <td className={cell}>{formatDuration(node.uptimeSeconds)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
